<script setup>
import ConfirmComponent from '@/components/ConfirmComponent.vue';
import ToastComponent from '@/components/ToastComponent.vue';
import { useAxios } from '@/hooks/useAxios';
import AppPopup from '@/layout/AppPopup.vue';
import { useAuthStore } from '@/store/auth-store';
import { computed, nextTick, onBeforeUnmount, ref } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const authStore = useAuthStore();
const isVisible = ref(false);
const userComponentRef = ref(null);
const userNameRef = computed(() => authStore.getUserName);
const userIdRef = computed(() => authStore.getUserId);
const popupHeight = ref('45rem');
const showPopup = ref(false);
const appStepperRef = ref(null);
const toastComponentRef = ref(null);
const showConfirmDialog = ref(false);
const confirmAction = ref(null);
const dialogHeader = ref('');
const dialogMessage = ref('');
const dialogcomplete = ref('');
let isConfirming = false;

defineProps(['class']);

const closePopup = () => {
    isVisible.value = false;
    unbindOutsideClickListener();
};

const openPopup = () => {
    isVisible.value = true;
    bindOutsideClickListener();
};

const bindOutsideClickListener = () => {
    document.addEventListener('click', outsideClickListener);
};

const unbindOutsideClickListener = () => {
    document.removeEventListener('click', outsideClickListener);
};

const outsideClickListener = (event) => {
    const panel = userComponentRef.value;
    const actionButtons = document.querySelectorAll('.action-button');
    if (panel && !panel.contains(event.target) && ![...actionButtons].some((btn) => btn.contains(event.target))) {
        closePopup();
    }
};

onBeforeUnmount(() => {
    unbindOutsideClickListener();
});

const handleLogout = async () => {
    try {
        await authStore.logout();
        closePopup();
        //router.push('/login'); // 로그아웃 후 로그인 페이지로 리다이렉트
    } catch (error) {
        console.error('Logout failed:', error);
    }
};

const handleMyinfo = async () => {
    //alert(userIdRef.value);

    showPopup.value = true;
    await nextTick();
    if (appStepperRef.value) {
        appStepperRef.value.setReadonly({ login_id: true });
        await fetchMyInfo();
    }
};

const fetchMyInfo = async () => {
    const url = `/api/manager/my`;
    const config = {
        method: 'get'
    };

    const { execute } = useAxios(url, config, {
        immediate: false,
        onSuccess(response) {
            appStepperRef.value.updateComponent(response.data.data[0]);
        },
        onError(err) {
            if (toastComponentRef.value) {
                toastComponentRef.value.showToast('error', '조회 실패', err.message);
            }
            console.error('Error:', err.message);
        }
    });

    await execute();
};

const stepperPanels = ref([
    {
        title: '나의정보',
        description: 'Step 1',
        content: [
            {
                marginTop: '1.2rem',
                components: [
                    {
                        type: 'inputbox',
                        props: {
                            inputId: 'login_id',
                            label: '아이디',
                            readonly: true
                        }
                    }
                ]
            },
            {
                marginTop: '1.2rem',
                components: [
                    {
                        type: 'inputbox',
                        props: {
                            inputId: 'manager_name',
                            label: '이름',
                            required: true,
                            placeholder: '이름을 입력 하세요.'
                        }
                    }
                ]
            },
            {
                marginTop: '1.2rem',
                components: [
                    {
                        type: 'inputbox',
                        props: {
                            inputId: 'phone',
                            label: '연락처',
                            required: true,
                            mask: 'phone',
                            placeholder: '연락처 입력'
                        }
                    }
                ]
            },
            {
                marginTop: '1.2rem',
                components: [
                    {
                        type: 'email',
                        props: {
                            inputId: 'email',
                            label: '이메일'
                        }
                    }
                ]
            },
            {
                marginTop: '1.2rem',
                components: [
                    {
                        type: 'inputbox',
                        props: {
                            inputId: 'position',
                            label: '직함',
                            placeholder: '직함을 입력 하세요.'
                        }
                    }
                ]
            }
        ],
        buttons: [
            {
                label: '패스워드 변경',
                onClick: () => {
                    appStepperRef.value.handleNavigation('next');
                }
            },
            {
                label: '저장',
                onClick: () => {
                    setDialogInfo('myinfo');
                    showConfirmDialog.value = true;
                }
            }
        ]
    },
    {
        title: '패스워드 변경',
        description: 'Step 1',
        content: [
            {
                marginTop: '1.2rem',
                components: [
                    {
                        type: 'inputbox',
                        props: {
                            inputId: 'old_password',
                            required: true,
                            label: '기존 패스워드',
                            mask: 'password',
                            placeholder: '기존 패스워드를 입력하세요.'
                        }
                    }
                ]
            },
            {
                marginTop: '1.2rem',
                components: [
                    {
                        type: 'inputbox',
                        props: {
                            inputId: 'new_password',
                            required: true,
                            label: '신규 패스워드',
                            mask: 'password',
                            placeholder: '영,숫자,특수문자 조합 8~16자리'
                        }
                    }
                ]
            },
            {
                marginTop: '1.2rem',
                components: [
                    {
                        type: 'inputbox',
                        props: {
                            inputId: 'password_confirm',
                            required: true,
                            label: '패스워드확인',
                            mask: 'password',
                            placeholder: '영,숫자,특수문자 조합 8~16자리'
                        },
                        inputEvent: (value) => {
                            if (appStepperRef.value.getModelValue('new_password') !== value.value) {
                                appStepperRef.value.setError({ password_confirm: '패스워드가 일치하지 않음' });
                            }
                        }
                    }
                ]
            }
        ],
        buttons: [
            {
                label: '나의정보 변경',
                onClick: () => {
                    appStepperRef.value.handleNavigation('prev');
                }
            },
            {
                label: '저장',
                onClick: () => {
                    if (appStepperRef.value.getModelValue('old_password') === '') {
                        appStepperRef.value.setError({ old_password: '기존 패스워드를 입력하세요.' });
                        return;
                    }

                    if (appStepperRef.value.getModelValue('new_password') === '') {
                        appStepperRef.value.setError({ new_password: '신규 패스워드를 입력하세요.' });
                        return;
                    }

                    if (appStepperRef.value.getModelValue('new_password') !== appStepperRef.value.getModelValue('password_confirm')) {
                        appStepperRef.value.setError({ password_confirm: '패스워드가 일치하지 않습니다.' });
                        if (toastComponentRef.value) {
                            toastComponentRef.value.showToast('error', '패스워드 변경 실패', '패스워드가 일치하지 않습니다.');
                        }
                        return;
                    }

                    setDialogInfo('password');
                    showConfirmDialog.value = true;
                }
            }
        ]
    }
]);

const setDialogInfo = (action) => {
    confirmAction.value = action;
    dialogHeader.value = dialogInfo[action].header;
    dialogMessage.value = dialogInfo[action].message;
    dialogcomplete.value = dialogInfo[action].completemessage;
};

const dialogInfo = {
    password: {
        header: '패스워드 초기화',
        message: '패스워드 초기화 하시겠습니까?',
        completemessage: '패스워드 초기화 되었습니다.\n(아이디와 동일합니다.)'
    },
    myinfo: {
        header: '나의정보 수정',
        message: '나의정보를 수정 하시겠습니까? ',
        completemessage: '나의정보가 수정되었습니다.'
    }
};

const handleConfirm = () => {
    if (isConfirming) return;
    isConfirming = true;

    let url = '';
    let config = null;
    let allVal = null;
    switch (confirmAction.value) {
        case 'password':
            url = `/api/manager/password`;
            if (appStepperRef.value) {
                allVal = appStepperRef.value.getStepComponentValues();
                delete allVal.password_confirm;
            }

            config = {
                method: 'put'
            };
            break;
        case 'myinfo':
            url = `/api/manager`;
            if (appStepperRef.value) {
                allVal = appStepperRef.value.getStepComponentValues();
                delete allVal.login_id;
                // allVal.name = allVal.manager_name;
                // delete allVal.manager_name;
            }

            config = {
                method: 'put'
            };
            break;
    }

    //console.log(allVal);

    try {
        const { data, error, execute } = useAxios(url, config, {
            immediate: false,
            onSuccess(response) {
                confirmSuccess();
            },
            onError(err) {
                if (appStepperRef.value) appStepperRef.value.setError(err.response.data.data);
            }
        });

        execute(allVal);
    } catch (error) {
        console.error('error', error);
    } finally {
        isConfirming = false;
    }
};

const confirmSuccess = () => {
    if (toastComponentRef.value) {
        toastComponentRef.value.showToast('success', dialogHeader.value, dialogcomplete.value);
    }

    showConfirmDialog.value = false;
    setTimeout(async () => {
        isConfirming = false;

        await fetchMyInfo();
    }, 1000);
};

const handleCancel = () => {
    if (isConfirming) return;
    showConfirmDialog.value = false;
};

defineExpose({
    openPopup,
    closePopup,
    isVisible
});
</script>

<template>
    <div ref="userComponentRef" class="user-popup" v-if="isVisible">
        <div class="user-content">
            <div class="button-container">
                <button class="user-button" @click="handleMyinfo">
                    <div class="button-content">
                        <i class="pi pi-user"></i>
                        <span>나의정보</span>
                    </div>
                </button>
                <button class="user-button" @click="handleLogout">
                    <div class="button-content">
                        <i class="pi pi-sign-out"></i>
                        <span>로그아웃</span>
                    </div>
                </button>
            </div>
        </div>
    </div>
    <AppPopup v-if="showPopup" ref="appStepperRef" v-model:isPopupVisible="showPopup" :panels="stepperPanels" :contentHeight="popupHeight" :nonButtonRefs="false" />
    <ToastComponent ref="toastComponentRef" />
    <ConfirmComponent
        v-model:visible="showConfirmDialog"
        :dialogHeader="dialogHeader"
        :dialogMessage="dialogMessage"
        dialogIcon="pi pi-exclamation-triangle"
        dialogWidth="400px"
        dialogButtonSeverity="danger"
        @confirm="handleConfirm"
        @cancel="handleCancel"
    />
</template>

<style scoped>
.user-popup {
    position: absolute;
    top: 3.5rem;
    right: 1.6rem;
    width: 9rem;
    height: 6.6rem;
    background-color: white;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    z-index: 300;
    display: flex;
    justify-content: center;
    align-items: center;
}

.user-content {
    width: 100%;
    height: 100%;
}

.button-container {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    width: 100%;
    height: 100%;
    padding: 0.4rem;
}

.user-button {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: white;
    border: none;
    padding: 0;
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: bold;
    transition: color 0.3s ease;
    flex: 1;
}

.user-button:hover {
    color: #0038ea;
    background-color: #f5f5f5;
    border-radius: 7px;
}

.button-content {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin: 0.4rem;
}

.user-button i {
    margin-right: 0.7rem;
    font-size: 1.2rem;
}
</style>
